import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import OfferteForm from '../../../components/feature/OfferteForm';

const WHATSAPP_URL = 'https://wa.me/31624359162?text=Hi%20Shubam%2C%20ik%20heb%20zojuist%20een%20offerte%20aanvraag%20ingediend%20en%20wil%20graag%20meer%20informatie%20over%20inkopen%20uit%20China.';

export default function CTASection() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section id="contact" ref={ref} className="relative py-24 overflow-hidden">
      <div className="absolute inset-0 bg-[#0A1628]" />
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/f3b25567cbdc0fbb40f18b7f0df3b8e8.jpg"
          alt=""
          className="w-full h-full object-cover object-top opacity-8"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#0D2147]/90 to-[#0A1628]" />
      </div>
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#3B9EE8]/5 rounded-full blur-3xl pointer-events-none -translate-y-1/2 translate-x-1/3" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#3B9EE8]/5 rounded-full blur-3xl pointer-events-none translate-y-1/3 -translate-x-1/4" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Linker kolom: tekst + voordelen */}
          <div>
            <motion.span
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-2 text-[#3B9EE8] text-sm font-600 mb-6"
            >
              <i className="ri-rocket-line" />
              Start vandaag nog
            </motion.span>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="font-heading font-800 text-white text-4xl md:text-5xl leading-tight mb-6"
            >
              Vraag gratis
              <br />
              <span className="text-[#3B9EE8]">prijscheck aan</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-white/60 text-lg leading-relaxed mb-8"
            >
              Ontvang binnen 24 uur een prijsindicatie direct uit China. Volledig gratis en zonder verplichtingen.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-col gap-4"
            >
              {[
                { icon: 'ri-time-line', text: 'Binnen 24 uur een prijsindicatie' },
                { icon: 'ri-checkbox-circle-line', text: 'Gratis en vrijblijvend' },
                { icon: 'ri-shield-check-line', text: 'Geen verplichtingen' },
                { icon: 'ri-truck-line', text: 'All-in DDP prijzen mogelijk' },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-[#3B9EE8] text-lg`} />
                  </div>
                  <span className="text-white/70 text-sm">{item.text}</span>
                </div>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.45 }}
              className="mt-8 p-5 border border-white/10 rounded-xl"
            >
              <p className="text-white/50 text-xs mb-3 uppercase tracking-widest">Of direct via WhatsApp</p>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="nofollow noreferrer"
                className="flex items-center gap-3 cursor-pointer group"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#25D366]/20 border border-[#25D366]/30 flex-shrink-0">
                  <i className="ri-whatsapp-line text-[#25D366] text-xl" />
                </div>
                <div>
                  <div className="text-white font-600 text-sm group-hover:text-[#3B9EE8] transition-colors">+31 6 24 35 91 62</div>
                  <div className="text-white/40 text-xs">Shubam &bull; Direct antwoord</div>
                </div>
                <i className="ri-arrow-right-line text-white/30 group-hover:text-[#3B9EE8] ml-auto transition-colors" />
              </a>
            </motion.div>
          </div>

          {/* Rechter kolom: formulier (gedeeld met /offerte pagina) */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-white rounded-2xl p-8 md:p-9">
              <div className="mb-6">
                <p className="text-[#3B9EE8] text-xs font-600 uppercase tracking-widest mb-1">
                  Ontvang binnen 24 uur een prijsindicatie
                </p>
                <h3 className="font-heading font-700 text-[#0A1628] text-xl">Gratis Prijscheck Aanvragen</h3>
              </div>
              <OfferteForm />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}